import { UAParser } from "ua-parser-js";
import { reportData } from "./dataReport";
import { reportGoogleConversion } from "./googleConversion";
import { ref } from "vue";

declare global {
	interface Window {
		MSStream?: any;
		opera?: any;
	}
}

interface DownloadResponse {
	// 根据实际返回数据结构定义
	[key: string]: any;
}

interface ApiResponse {
	payload: {
		client_version_list: DownloadResponse[];
	};
}

interface DeviceInfo {
	isIOS: boolean;
	isAndroid: boolean;
	isWeChat: boolean;
	isMobile: boolean;
}

interface DownloadStatus {
	isLoading: boolean;
	error: string | null;
	success: boolean;
}

const downloadDataPromise: Promise<any> | null = null;
let api = "";

// 获取当前语言环境
const getCurrentLocale = (): string => {
	if (typeof window === "undefined") return "ja";

	// 从URL路径获取语言
	const pathname = window.location.pathname;
	if (pathname.startsWith("/en")) return "en";
	if (pathname.startsWith("/hant")) return "hant";

	// 从localStorage获取语言
	const savedLang = localStorage.getItem("lang");
	if (savedLang && ["ja", "en", "hant"].includes(savedLang)) {
		return savedLang;
	}

	// 默认返回日语
	return "ja";
};

// 从 URL 获取 channel 参数
export const getChannel = () => {
	if (typeof window !== "undefined") {
		const queryParams = new URLSearchParams(window.location.search);
		return queryParams.get("channel") || "default";
	}
	return "default";
};

const channel = getChannel();

export const downloadStatus = ref<DownloadStatus>({
	isLoading: false,
	error: null,
	success: false,
});

export const downloadData = ref<string>("");

export const getDownloadData = async (apiBaseUrl: string): Promise<ApiResponse> => {
	api = apiBaseUrl;
	try {
		return await $fetch(`${apiBaseUrl}/api/client/get_latest_client_version`, {
			method: "GET",
		});
	} catch (error) {
		console.error("获取下载数据失败:", error);
		throw error;
	}
};

export const setDownloadData = (data: DownloadResponse) => {
	// 从url获取channel
	const channel = getChannel();
	downloadData.value = `https://download.hakko.ai/beta/jpn_online/hakkoai_${channel}_ai.exe`;
};

export const handleDownloadPC = async (locale?: string) => {
	reportData("HakkoAIDownloadPC", {
		channel: channel,
	});
	try {
		if (downloadData.value) {
			downloadFile(downloadData.value, "");
			// 获取当前语言环境，默认为日语
			const currentLocale = locale || getCurrentLocale();
			// 触发Google转化追踪
			reportGoogleConversion(currentLocale, undefined, "PC下载");
		} else {
			throw new Error("没有可用的下载链接");
		}
	} catch (error) {
		console.error("PC下载失败:", error);
		throw error;
	}
};

// 下载文件
const downloadFile = (url: string, fileName: string) => {
	if (typeof window === "undefined") return;

	const a = document.createElement("a");
	a.href = url;
	a.download = fileName || "download";
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
};

const getUAData = () => {
	// 上报归因
	const parser = new UAParser();
	const result = parser.getResult();
	// iOS检测;
	if (/iPad|iPhone|iPod/.test(result.ua) && !window.MSStream) {
		console.log("iOS设备");
		let deviceNameForUserAgent = result.device.model || "",
			osNameForUserAgent = result.device.model ? result.device.model + " OS" : "",
			osMarketingVersionString = result.os.version ? result.os.version.replace(/\./g, "_") : "",
			webKitVersion = result.engine.version,
			applicationName = (result.ua.match(/Mobile\/(\d+[A-Za-z0-9]+)?/) || [])[0] || "";
		return `Mozilla/5.0 (${deviceNameForUserAgent}; CPU ${osNameForUserAgent} ${osMarketingVersionString} like Mac OS X) AppleWebKit/${webKitVersion} (KHTML, like Gecko) ${applicationName}`;
	}
	// Android检测
	if (/android/i.test(result.ua)) {
		console.log("Android设备");
		let osName = result.os.name || "",
			osVersion = result.os.version || "",
			deviceName = result.device.model || "",
			applicationName = (result.ua.match(/Build\/([^\s\)]*)/) || [])[0] || "";
		return `Mozilla/5.0 (Linux; ${osName} ${osVersion}; ${deviceName} ${applicationName};)`;
	}
	return result.ua;
};

// 检查用户广告信息
const checkUserAdInfo = async () => {
	if (typeof window === "undefined") return;

	try {
		const userAgent = navigator.userAgent || navigator.vendor || window.opera;
		const ua_xy = getUAData();

		const ip = await fetch("https://api.ipify.org?format=json")
			.then(response => response.json())
			.then(data => data.ip)
			.catch(() => "");

		const data = {
			conv_time: 0,
			idfa: "",
			client_ip: ip,
			model: "",
			ua: userAgent,
			oaid: "",
			updatetime: "",
			boottime: "",
			report_from: channel,
			ua_xy,
		};

		const fetchOptions: RequestInit = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			redirect: "follow",
			referrerPolicy: "no-referrer",
		};

		await fetch(`${api}/api/utils/huoban_ad_report`, fetchOptions);
	} catch (error) {
		console.error("上报广告信息失败:", error);
	}
};

// iOS下载
const handleDownloadByIos = async () => {
	try {
		downloadStatus.value.isLoading = true;
		downloadStatus.value.error = null;

		// 检查是否在微信浏览器中
		const isWeChatBrowser = /MicroMessenger/i.test(navigator.userAgent);
		const appStoreUrl = "https://apps.apple.com/jp/app/id6476439115";

		if (isWeChatBrowser) {
			// 在微信中打开外部浏览器
			window.location.href = "https://support.weixin.qq.com/guide/wechat/wechat_ios_update";
		} else {
			await checkUserAdInfo();
			// 直接跳转到 App Store
			window.location.href = appStoreUrl;
			// 上报下载事件
		}

		downloadStatus.value.success = true;
	} catch (error) {
		downloadStatus.value.error = error instanceof Error ? error.message : "下载失败";
		console.error("iOS下载错误:", error);
	} finally {
		downloadStatus.value.isLoading = false;
	}
};

// 安卓下载
const goToGooglePlay = async () => {
	const packageName = "ai.hakko.HakkoAI";
	const storeUrl = `https://play.google.com/store/apps/details?id=${packageName}`;

	// 判断是否移动端
	const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

	if (isMobile) {
		// 尝试深层链接（如果已安装应用）
		const deepLink = `market://details?id=${packageName}`;
		window.location.href = deepLink;

		// 设置备用方案（如果深层链接失败）
		setTimeout(async () => {
			await checkUserAdInfo();
			if (!document.hidden) {
				window.location.href = storeUrl;
			}
		}, 500);
	} else {
		// PC端直接打开网页版商店
		window.open(storeUrl, "_blank");
	}
};

// 判断设备类型
const getDeviceType = (): DeviceInfo => {
	const ua = navigator.userAgent.toLowerCase();

	// iOS 设备判断
	const isIOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;

	// Android 设备判断
	const isAndroid = /android/.test(ua) && !/windows phone/.test(ua);

	// 微信浏览器判断
	const isWeChat = /micromessenger/.test(ua);

	return {
		isIOS,
		isAndroid,
		isWeChat,
		isMobile: isIOS || isAndroid,
	};
};

// 移动端下载
export const handleDownloadApp = async () => {
	try {
		downloadStatus.value.isLoading = true;
		downloadStatus.value.error = null;

		const { isIOS, isAndroid, isWeChat } = getDeviceType();

		if (isIOS) {
			await handleDownloadByIos();
			reportData("HakkoAIDownloadApp", {
				channel: channel,
			});
		} else if (isAndroid) {
			await goToGooglePlay();
		} else {
			downloadStatus.value.error = "不支持的设备类型";
		}
		reportData("HakkoAIDownloadApp", {
			platform: isIOS ? "ios" : isAndroid ? "android" : "unknown",
			channel: channel,
		});
	} catch (error) {
		downloadStatus.value.error = error instanceof Error ? error.message : "下载失败";
		console.error("下载错误:", error);
	} finally {
		downloadStatus.value.isLoading = false;
	}
};
